import {
    INVOICE_CLASSIFICATION_MODEL,
    INVOICE_CLASSIFICATION_PROMPT,
    INVOICE_CLASSIFICATION_JSON_SCHEMA,
    POSSIBLE_MIME_TYPES,
} from '~common/invoice-processing/constants'
import type {
    GmailMessageAttachmentDetails,
    GmailMessageParsed,
} from '~common/google-api/types'
import type {
    InvoiceProcessorConfig,
    ProcessInvoiceReturn,
    ProcessEmailParams,
    InvoicePDFAttachmentData,
    ProcessEmailCrudeReturn,
    ProcessEmailIntermediateResult,
} from './types'
import type {
    RawInvoiceMetadata,
    InvoiceClass,
    InvoiceClassificationResult,
} from '~common/invoice-processing/types'
import {
    AbortError,
    assertNotAborted,
    AsyncQueue,
    SkipRetryError,
} from '~/features/async-queue'
import type { PersistedQueueState } from '~/features/async-queue/types'
import type { Services } from '~/services/types'
import { base64ToBlob } from '~common/utils/blob-utils'
import type { PdfToTextMode } from '../pdf-to-text'
import type {
    StorageModules,
    Invoice,
    LocalStorageModules,
} from '~/storage/types'
import type { StatePersister } from '~/features/state-persistence/types'
import { parseFullMessage } from '~common/google-api/utils'
import { retryWithValidation } from '~/utils/retry-with-validation'
import { htmlToText } from '../html-to-text'
import { detectStripeEmail } from '../utils'
import type { PersistedImportState } from '~/features/email-importer/services/types'
import { fromPairs } from '~/utils/from-pairs'
import { postProcessLLMInvoiceDataExtraction } from '~common/invoice-processing/post-processing'
import { InvoiceExtractionService } from '~common/invoice-processing/service'
import type { ParsedInvoiceMetadata } from '~common/invoice-processing/types'
import { createEmailPDFFileName } from '~common/invoice-processing/createEmailPDFFileName'
import { Email, EmailAnalysisStatus } from '~common/storage/email'
import type {
    LLMRequestMessage,
    LLMRequestContent,
    LLMResponseJSONSchema,
    LLMPlugin,
} from '~common/llms/types'

export interface InvoiceProcessorDeps {
    getServices: () => Pick<
        Services,
        | 'llms'
        | 'googleApi'
        | 'errorTracker'
        | 'cache'
        | 'favicon'
        | 'invoiceVendorMatching'
        | 'entitlements'
        | 'globalEvents'
        | 'cloudFunctions'
        | 'transactionInvoiceMatching'
    >
    pdfFileToText: (pdfData: Blob, mode: PdfToTextMode) => Promise<string>
    storageModules: StorageModules
    localStorageModules: LocalStorageModules
    classifyEmailAsInvoice: (emailText: string) => InvoiceClassificationResult
    config: InvoiceProcessorConfig
    importerStatePersister: StatePersister<PersistedImportState>
    invoiceProcessingQueueStatePersister: StatePersister<
        PersistedQueueState<Email>
    >
}

export class FrontendInvoiceProcessorService {
    readonly invoiceProcessingQueue: AsyncQueue<Email, ProcessInvoiceReturn>
    private lastImportStateUpdatePromise: Promise<void> = Promise.resolve()

    constructor(private readonly deps: InvoiceProcessorDeps) {
        const services = deps.getServices()
        this.invoiceProcessingQueue = new AsyncQueue(
            this.processEmailForInvoices.bind(this),
            {
                queueName: 'invoice-processing-queue',
                statePersister: this.deps.invoiceProcessingQueueStatePersister,
                autoStart: true,
                maxConcurrency: 10,
                retriesExceededBehavior: 'remove',
                maxRetries: 3,
                onItemProcessed: async () => {
                    await this.updateImportState((state) => {
                        state.invoiceCandidatesProcessedThisRun += 1
                    })
                },
                onItemError: async (params, error) => {
                    services.errorTracker.trackError(
                        `Invoice processing queue: Failed to process invoice attachment for email ${params.id}`,
                        error,
                        {
                            emailId: params.id,
                        },
                    )
                },
                onQueueEmpty: async () => {
                    const importStats =
                        await this.deps.importerStatePersister.hydrate()
                    if (importStats) {
                        if (importStats.status.scan === 'running') {
                            return
                        } else {
                            const queueEmpty =
                                this.invoiceProcessingQueue.isQueueEmpty()
                            if (queueEmpty) {
                                this.invoiceProcessingQueue.stop({
                                    skipTaskAbort: true,
                                })
                                importStats.status.import = 'completed'
                                await this.deps.importerStatePersister.persist(
                                    importStats,
                                )
                                services.globalEvents.emit({
                                    type: 'updateImportState',
                                    state: importStats,
                                })
                            }
                        }
                    }
                },
                onBeforeItemProcess: async (email: Email) => {
                    const services = this.deps.getServices()
                    const remainingInvoiceCredits =
                        await services.entitlements.getRemainingInvoiceCredits()
                    if (remainingInvoiceCredits === 0) {
                        throw new Error('No remaining invoice credits')
                    }
                },
            },
        )
    }

    private async updateImportState(
        updater: (state: PersistedImportState) => void,
    ): Promise<void> {
        this.lastImportStateUpdatePromise =
            this.lastImportStateUpdatePromise.then(async () => {
                const importStats =
                    await this.deps.importerStatePersister.hydrate()
                if (importStats) {
                    updater(importStats)
                    await this.deps.importerStatePersister.persist(importStats)
                    this.deps.getServices().globalEvents.emit({
                        type: 'updateImportState',
                        state: importStats,
                    })
                }
            })
        return this.lastImportStateUpdatePromise
    }

    private async checkAndExecutePaywallOnSuccessfulInvoiceProcessing(returnValue: {
        newInvoices: number
    }): Promise<void> {
        const remainingInvoiceCredits = await this.deps
            .getServices()
            .entitlements.checkAndOrUpdateEntitlement({
                dontNotify: true,
                update: true,
                newInvoices: returnValue.newInvoices,
            })

        if (remainingInvoiceCredits.remainingCredits === 25) {
            this.deps.getServices().globalEvents.emit({
                type: 'openModal',
                modalType: '25-left-notif',
            })
            this.invoiceProcessingQueue.stop()
            this.invoiceProcessingQueue.clear()
        }
        if (remainingInvoiceCredits.allowed) {
            const importStats = await this.deps.importerStatePersister.hydrate()
            if (importStats) {
                importStats.actualInvoicesFoundThisRun += 1
                this.deps
                    .getServices()
                    .cache.setKey('importProgress', importStats)
                await this.deps.importerStatePersister.persist(importStats)
            }
        }
    }

    async processEmailsForStorage({
        emails,
        dontProcessInvoices,
        now,
    }: ProcessEmailParams): Promise<Email[]> {
        if (emails.length === 0) {
            return []
        }
        const { storageModules, localStorageModules } = this.deps
        const services = this.deps.getServices()
        const emailsByExternalIds = fromPairs(
            emails.map((email) => [email?.externalId, email]),
        )

        const externalIds = emails.map((email) => email?.externalId)
        const existingEmailsMap =
            await storageModules.emailInvoice.getEmailsByExternalIds(
                externalIds,
            )

        const processedEmails: Email[] = []
        const emailsToStore: ProcessEmailCrudeReturn['email'][] = []

        for (const email of emails) {
            // TODO-audit: need to keep track of the state of the favicon fetch
            this.queueVendorFaviconFetch(email.fromEmail)
            const existingEmail = existingEmailsMap[email.externalId]

            if (
                existingEmail &&
                existingEmail.analysisStatus === 'full-success'
            ) {
                // TODO-audit: Move this to happen in UI
                services.cache.setEmail([existingEmail])
                processedEmails.push(existingEmail)
                continue
            }

            let classificationResult = this.deps.classifyEmailAsInvoice(
                email?.subject + ' ' + email?.snippet,
            )

            // TODO-audit: why isn't this done in the crude classification?
            const hasInvoiceBasedOnSpecialRules =
                this.specialInvoiceRules(email)
            if (!hasInvoiceBasedOnSpecialRules) {
                classificationResult.classification = 'other'
                classificationResult.confidence = 1
            }

            const isStripeEmail = detectStripeEmail(email)
            const relevantAttachments = email?.attachments
                ?.map((attachment?: GmailMessageAttachmentDetails) => {
                    if (
                        (attachment?.mimeType &&
                            !POSSIBLE_MIME_TYPES.includes(
                                attachment?.mimeType,
                            )) ||
                        !attachment?.filename?.toLowerCase()?.includes('.pdf')
                    ) {
                        return null
                    }

                    // TODO-audit: These conditions are *very* specific, as if they're targetting particular types of email attachments
                    //  If so, it needs to move into it's own util function as very unlikely it'll stay this simple
                    const hasInvoiceDocument = email?.attachments?.some(
                        (attachment) =>
                            attachment?.mimeType &&
                            POSSIBLE_MIME_TYPES.includes(
                                attachment?.mimeType,
                            ) &&
                            attachment?.filename
                                ?.toLowerCase()
                                ?.includes('invoice-'),
                    )
                    if (
                        isStripeEmail &&
                        attachment?.filename
                            ?.toLowerCase()
                            ?.includes('receipt-') &&
                        hasInvoiceDocument
                    ) {
                        return null
                    }

                    return attachment
                })
                .filter((attachment) => attachment != null)

            for (const attachment of relevantAttachments) {
                if (attachment?.filename?.toLowerCase()?.includes('invoice-')) {
                    classificationResult.classification = 'invoice'
                    classificationResult.confidence = 1
                }
            }

            if (!this.deps.getServices()?.cache?.getKey('activeGmailAccount')) {
                throw new Error('No active Gmail account')
            }

            if (classificationResult.classification === 'other') {
                const tempEmail: Email = {
                    id: 'temp-' + Math.random().toString(36).substring(2, 15),
                    externalId: email.externalId,
                    accountEmail: this.deps
                        .getServices()
                        .cache.getKey('activeGmailAccount') as string,
                    subject: email.subject,
                    snippet: email.snippet,
                    receivedDate: email.receivedDate,
                    fromName: email.fromName,
                    fromEmail: email.fromEmail,
                    originalAttachments: relevantAttachments,
                    analysisStatus: 'full-success',
                    invoiceClassification: 'other',
                    invoiceClassificationConfidence: 1,
                    createdAt: Date.now(),
                    updatedAt: Date.now(),
                }

                // TODO-audit: Move this to happen in UI
                services.cache.setEmail([tempEmail])
                processedEmails.push(tempEmail)
                continue
            }

            emailsToStore.push({
                externalId: email.externalId,
                subject: email.subject,
                accountEmail: this.deps
                    .getServices()
                    .cache.getKey('activeGmailAccount') as string,
                snippet: email.snippet,
                receivedDate: email.receivedDate,
                fromName: email.fromName,
                fromEmail: email.fromEmail,
                originalAttachments: relevantAttachments,
                analysisStatus: 'crude-success' as EmailAnalysisStatus,
                invoiceClassification: classificationResult.classification,
                invoiceClassificationConfidence:
                    classificationResult.confidence,
            })
        }

        if (emailsToStore.length > 0) {
            const storedEmails =
                await storageModules.emailInvoice.storeParsedEmails({
                    emails: emailsToStore,
                    now,
                })

            // Sort to prioritize emails with more attachments for enqueuing
            storedEmails.sort(
                (a, b) =>
                    b.originalAttachments.length - a.originalAttachments.length,
            )

            // Cache stored emails and set HTML cache
            for (const storedEmail of storedEmails) {
                // TODO-audit: Move this to happen in UI
                services.cache.setEmail([storedEmail])

                const originalEmail =
                    emailsByExternalIds[storedEmail.externalId]
                if (originalEmail?.htmlContent) {
                    await localStorageModules.emailHtmlCache.setHtml(
                        storedEmail.externalId,
                        originalEmail.htmlContent,
                    )
                }

                processedEmails.push(storedEmail)

                if (dontProcessInvoices) {
                    continue
                }

                const remainingInvoiceCredits =
                    await services.entitlements.getRemainingInvoiceCredits()
                // TODO-audit: why is this number hardcoded? Guessing it's the initial import limit
                if (remainingInvoiceCredits <= 25) {
                    continue
                }

                this.invoiceProcessingQueue.enqueue(
                    storedEmail,
                    storedEmail.externalId,
                    Boolean(storedEmail.originalAttachments?.length > 0),
                )
            }
        }

        return processedEmails
    }

    async scheduleExistingInvoices(): Promise<number> {
        const remainingInvoiceCredits = await this.deps
            .getServices()
            .entitlements.getRemainingInvoiceCredits()
        if (remainingInvoiceCredits >= 25) {
            const existingEmails =
                await this.deps.storageModules.emailInvoice.getUnprocessedInvoiceEmails()
            for (const email of existingEmails) {
                this.invoiceProcessingQueue.enqueue(
                    email,
                    email.externalId,
                    Boolean(email.originalAttachments?.length > 0),
                )
            }

            return existingEmails.length
        }
        return 0
    }

    async resumeQueue() {
        const remainingInvoiceCredits = await this.deps
            .getServices()
            .entitlements.getRemainingInvoiceCredits()
        if (remainingInvoiceCredits >= 25) {
            this.invoiceProcessingQueue.resume()
        }
        const currentImportStats =
            await this.deps.importerStatePersister.hydrate()
        if (currentImportStats) {
            if (
                currentImportStats.status.paused ||
                currentImportStats.status.scan === 'error'
            ) {
                this.deps.getServices().globalEvents.emit({
                    type: 'startImporter',
                })
            }
        }
    }

    async analyzeSingleMessageForInvoices(email: Email): Promise<void> {
        const services = this.deps.getServices()
        let emailRecord: Record<string, Email> | undefined =
            await this.deps.storageModules.emailInvoice.getEmailsByExternalIds([
                email.externalId,
            ])

        let message: Email = emailRecord[email.externalId]

        if (!message) {
            const result =
                await this.deps.storageModules.emailInvoice.storeParsedEmail({
                    email: {
                        ...email,
                        id: undefined,
                    },
                    now: Date.now(),
                })
            if (!result?.id) {
                throw new Error('Failed to store email')
            }

            const newEmail = {
                ...email,
                id: result?.id,
            }

            if (email.id?.startsWith('temp-')) {
                await services.cache.replaceEmail(email.id, newEmail)
            }
            message = {
                ...email,
                id: result?.id,
            }
        }

        this.deps.getServices().cache.setEmail([
            {
                ...message,
                id: message.id,
                analysisStatus: 'running',
            },
        ])
        try {
            await this.processEmailForInvoices(message)
        } catch (error) {
            this.deps.getServices().cache.setEmail([
                {
                    ...message,
                    id: message.id,
                    analysisStatus: 'error',
                },
            ])
        }
    }

    async processEmailForInvoices(
        email: Email,
        signal?: AbortSignal,
        skipPrefilter?: boolean,
    ): Promise<ProcessInvoiceReturn> {
        const services = this.deps.getServices()

        assertNotAborted(signal)

        const attachmentsToProcess: InvoicePDFAttachmentData[] = []
        for (const attachment of email.originalAttachments) {
            if (!POSSIBLE_MIME_TYPES.includes(attachment.mimeType)) {
                continue
            }

            const payload = await services.googleApi.downloadGmailAttachment({
                messageId: email.externalId,
                attachmentId: attachment.id,
                accountEmail: email.accountEmail,
            })
            if (payload.data != null && payload.size > 0 && attachment.id) {
                attachmentsToProcess.push({
                    id: attachment.id,
                    filename: attachment.filename,
                    data: payload.data,
                    size: payload.size,
                })
            }
        }

        assertNotAborted(signal)
        let successfulInvoicesCount = 0

        // If no attachments, process the email as the invoice itself...
        if (attachmentsToProcess.length === 0) {
            const intermediateResult = await this.processInvoiceEmail(
                email,
                signal,
                skipPrefilter,
            )
            assertNotAborted(signal)
            if (
                intermediateResult?.invoiceMetadata &&
                intermediateResult?.googleDriveFileId
            ) {
                await this.processInvoiceFromText({
                    ...intermediateResult,
                    email,
                })
                successfulInvoicesCount += 1
            } else {
                if (signal) {
                    throw new SkipRetryError()
                }
                services.cache.setEmail([
                    {
                        id: email.id,
                        analysisStatus: 'full-success',
                        invoiceClassification: 'other',
                        invoiceClassificationConfidence: 1,
                    },
                ])
                return { newInvoices: 0 }
            }
        }

        for (const attachment of attachmentsToProcess) {
            assertNotAborted(signal)
            const intermediateResult = await this.processInvoiceEmailAttachment(
                attachment,
                signal,
            )
            if (
                intermediateResult?.invoiceMetadata &&
                intermediateResult?.googleDriveFileId
            ) {
                assertNotAborted(signal)
                await this.processInvoiceFromText({
                    ...intermediateResult,
                    email,
                })
                successfulInvoicesCount += 1
            }
        }

        assertNotAborted(signal)

        const noInvoices = successfulInvoicesCount === 0

        const classification = noInvoices ? 'other' : 'invoice'
        services.cache.setEmail([
            {
                id: email.id,
                analysisStatus: 'full-success',
                invoiceClassification: classification,
                invoiceClassificationConfidence: 1,
            },
        ])

        await this.deps.storageModules.emailInvoice.updateParsedEmail({
            emailId: email.id,
            analysisStatus: 'full-success',
            // TODO-audit: classification confidence looks like it's now static (?)
            invoiceClassification: {
                classification,
                confidence: 1,
            },
        })

        if (noInvoices) {
            if (signal) {
                throw new SkipRetryError()
            }
        }

        return { newInvoices: successfulInvoicesCount }
    }

    private async processInvoiceEmail(
        email: Email,
        signal?: AbortSignal,
        skipPrefilter?: boolean,
    ): Promise<ProcessEmailIntermediateResult | undefined> {
        const services = this.deps.getServices()
        try {
            assertNotAborted(signal)

            const emailHTML = await this.fetchExternalEmailHTML(
                email.externalId,
                email.accountEmail,
            )
            if (!emailHTML) {
                if (signal) {
                    throw new SkipRetryError()
                }
            } else {
                assertNotAborted(signal)

                const pdfText = htmlToText(emailHTML)
                const pdfTextSample = pdfText.slice(0, 500)

                // Classify the text to determine if it's an invoice
                let classification: InvoiceClassificationResult | undefined =
                    undefined
                if (skipPrefilter) {
                    classification = {
                        classification: 'invoice',
                        confidence: 1,
                    }
                } else {
                    classification =
                        await this.classifyPotentialInvoiceTextViaLLM(
                            pdfTextSample,
                        )
                }
                if (!classification) {
                    if (signal) {
                        throw new SkipRetryError()
                    }
                    return
                }
                if (classification.classification === 'other') {
                    if (signal) {
                        throw new SkipRetryError()
                    }
                }

                assertNotAborted(signal)

                const extractedData = await this.extractInvoiceData(pdfText)
                if (!extractedData) {
                    if (signal) {
                        throw new SkipRetryError()
                    }
                    return
                }

                const invoiceMetadata = postProcessLLMInvoiceDataExtraction(
                    pdfText,
                    extractedData,
                )

                assertNotAborted(signal)

                // Pre-generate a Google Drive file ID
                const fileIds = await services.googleApi.generateDriveFileIds(1)
                const preGeneratedFileId = fileIds[0]
                if (!preGeneratedFileId) {
                    throw new Error('Failed to generate Drive file ID')
                }

                if (!invoiceMetadata) {
                    return undefined
                }

                const result = await services.cloudFunctions.processHTMLToPDF({
                    html: emailHTML,
                    invoiceDate: invoiceMetadata.date,
                    fileName: createEmailPDFFileName(email),
                    fileId: preGeneratedFileId,
                    storeInGoogleDrive: true,
                })

                if (result.success === false) {
                    throw new Error(result.error ?? 'Failed to generate PDF')
                }

                // Return immediately with the pre-generated file ID
                return {
                    invoiceMetadata,
                    googleDriveFileId: preGeneratedFileId,
                }
            }
        } catch (error) {
            if (
                !(error instanceof SkipRetryError) &&
                !(error instanceof AbortError)
            ) {
                services.errorTracker.throwErrorWithNotification(
                    error instanceof Error
                        ? error.message
                        : 'Failed to process invoice email',
                    error instanceof Error ? error : new Error(String(error)),
                )
            }
            throw error
        }
    }

    private async processInvoiceEmailAttachment(
        attachment: InvoicePDFAttachmentData,
        signal?: AbortSignal,
    ): Promise<ProcessEmailIntermediateResult | undefined> {
        assertNotAborted(signal)
        const services = this.deps.getServices()

        let pdfFile = base64ToBlob(attachment.data)
        let pdfText = await this.deps.pdfFileToText(pdfFile, 'full')
        if (pdfText.length === 0) {
            const { llms } = this.deps.getServices()
            const adaptedGetReply = <T = unknown>(params: {
                model: string
                messages: LLMRequestMessage<string | LLMRequestContent[]>[]
                jsonResponseSchema?: LLMResponseJSONSchema
                plugins?: LLMPlugin[]
            }) =>
                llms.getReply<T>({
                    ...params,
                    stream: false,
                    onlyGoogleVertex: true,
                }) as Promise<T>
            const invoiceExtractionService = new InvoiceExtractionService({
                getReply: adaptedGetReply,
            })
            pdfText =
                await invoiceExtractionService.extractPdfTextFromImage(pdfFile)
        }
        const pdfTextSample = pdfText.slice(0, 500)
        assertNotAborted(signal)

        const { classification } =
            await this.classifyPotentialInvoiceTextViaLLM(pdfTextSample)

        if (classification === 'other') {
            throw new SkipRetryError()
        }

        assertNotAborted(signal)

        const extractedData = await this.extractInvoiceData(pdfText)
        if (!extractedData) {
            throw new SkipRetryError()
        }
        const invoiceMetadata = postProcessLLMInvoiceDataExtraction(
            pdfText,
            extractedData,
        )
        assertNotAborted(signal)
        // Pre-generate a Google Drive file ID
        const fileIds = await services.googleApi.generateDriveFileIds(1)
        const preGeneratedFileId = fileIds[0]
        if (!preGeneratedFileId) {
            throw new Error('Failed to generate Drive file ID')
        }

        if (!invoiceMetadata) {
            return undefined
        }

        // Write the actual PDF to storage (non-blocking)
        const pdfName = attachment.filename
        const invoiceDate = new Date(invoiceMetadata.date)
            .toISOString()
            .split('T')[0]

        services.googleApi
            .uploadToExternalStorage({
                filename: pdfName,
                data: pdfFile,
                date: invoiceDate,
                fileId: preGeneratedFileId,
            })
            .catch((error) => {
                services.errorTracker.throwErrorWithNotification(
                    error instanceof Error
                        ? error.message
                        : 'Failed to upload PDF to Drive/External Storage',
                    error instanceof Error ? error : new Error(String(error)),
                )
            })

        // Return immediately with the pre-generated file ID
        return {
            invoiceMetadata,
            googleDriveFileId: preGeneratedFileId,
        }
    }

    private async processInvoiceFromText({
        email,
        invoiceMetadata,
        googleDriveFileId,
        now,
    }: ProcessEmailIntermediateResult & {
        email: Email
        now?: number
    }): Promise<{ emailInternalId: string }> {
        const services = this.deps.getServices()
        const { storageModules, localStorageModules } = this.deps

        now = now ?? Date.now()

        // Default to the email's "from" email if no vendor email addresses are provided
        if (
            !invoiceMetadata.vendorEmailAddresses ||
            invoiceMetadata.vendorEmailAddresses.length === 0
        ) {
            const emailData = await storageModules.emailInvoice.getEmailById(
                email.id,
            )
            const fromEmail = emailData?.fromEmail
            if (fromEmail) {
                invoiceMetadata.vendorEmailAddresses = [fromEmail]
            }
        }

        const existingEmailInvoicePairs = services.cache.getKey('emailInvoices')
        const existingInvoicesForEmail = existingEmailInvoicePairs[email.id]

        const invoice = await this.storeAndCountInvoice(
            now,
            googleDriveFileId,
            email,
            invoiceMetadata,
        )

        localStorageModules.emailHtmlCache.deleteHtml(email.id)

        services.cache.setEmailInvoices([
            {
                emailId: email.id,
                invoiceIds: [invoice.id, ...(existingInvoicesForEmail ?? [])],
            },
        ])

        return { emailInternalId: email.id }
    }

    async processInvoiceFromFileUpload(file: File): Promise<
        | {
              invoiceMetadata: Invoice
              googleDriveFileId: string
          }
        | undefined
    > {
        const services = this.deps.getServices()

        let pdfText = await this.deps.pdfFileToText(file, 'full')
        console.log('pdfText', pdfText)
        if (pdfText.length === 0) {
            const { llms } = this.deps.getServices()
            const adaptedGetReply = <T = unknown>(params: {
                model: string
                messages: LLMRequestMessage<string | LLMRequestContent[]>[]
                jsonResponseSchema?: LLMResponseJSONSchema
                plugins?: LLMPlugin[]
            }) =>
                llms.getReply<T>({
                    ...params,
                    stream: false,
                    onlyGoogleVertex: true,
                }) as Promise<T>
            const invoiceExtractionService = new InvoiceExtractionService({
                getReply: adaptedGetReply,
            })
            pdfText =
                await invoiceExtractionService.extractPdfTextFromImage(file)
        }
        const pdfTextSample = pdfText.slice(0, 500)

        const { classification } =
            await this.classifyPotentialInvoiceTextViaLLM(pdfTextSample)
        console.log('classification', classification)

        if (classification === 'other') {
            return
        }

        const extractedData = await this.extractInvoiceData(pdfText)
        console.log('extractedData', extractedData)
        if (!extractedData) {
            return
        }
        const invoiceMetadata = postProcessLLMInvoiceDataExtraction(
            pdfText,
            extractedData,
        )
        console.log('invoiceMetadata', invoiceMetadata)

        if (!invoiceMetadata) {
            throw new Error('Failed to process invoice', )
        }

        // Pre-generate a Google Drive file ID
        const fileIds = await services.googleApi.generateDriveFileIds(1)
        const preGeneratedFileId = fileIds[0]
        if (!preGeneratedFileId) {
            throw new Error('Failed to generate Drive file ID')
        }

        // Write the actual PDF to storage (non-blocking)
        const pdfName = file.name
        const invoiceDate = new Date(invoiceMetadata.date)
            .toISOString()
            .split('T')[0]

            console.log('invoiceDate', invoiceDate)

        services.googleApi
            .uploadToExternalStorage({
                filename: pdfName,
                data: file,
                date: invoiceDate,
                fileId: preGeneratedFileId,
            })
            .catch((error) => {
                services.errorTracker.throwErrorWithNotification(
                    error instanceof Error
                        ? error.message
                        : 'Failed to upload PDF to Drive/External Storage',
                    error instanceof Error ? error : new Error(String(error)),
                )
            })

        const invoice = await this.storeAndCountInvoice(
            Date.now(),
            preGeneratedFileId,
            undefined,
            invoiceMetadata,
        )

        // Return immediately with the pre-generated file ID
        return {
            invoiceMetadata: invoice,
            googleDriveFileId: preGeneratedFileId,
        }
    }

    async storeAndCountInvoice(
        now: number,
        googleDriveFileId: string,
        email: Email | undefined,
        invoiceMetadata: ParsedInvoiceMetadata,
    ): Promise<Invoice> {
        const services = this.deps.getServices()
        const { storageModules } = this.deps

        const dbResult = await storageModules.emailInvoice.storeInvoiceData({
            now,
            fileId: googleDriveFileId,
            emailId: email?.id ?? undefined,
            metadata: invoiceMetadata,
            vendorSuggestions: [],
        })

        // Construct the full invoice object for cache storage
        const invoice: Invoice = {
            id: dbResult.id,
            sourceEmailId: email?.id ?? null,
            vendorId: null,
            vendorLinkConfirmed: false,
            googleDriveFileId,
            sourceType: email?.id ? 'email' : 'upload',
            fileType: 'pdf',
            metadata: invoiceMetadata,
            vendorSuggestions: [],
            createdAt: now,
            updatedAt: now,
        }

        // TODO-audit: why isn't this being awaited?
        services.transactionInvoiceMatching.findMatchesForInvoice(invoice.id)
        services.invoiceVendorMatching.matchInvoiceVendors(invoice)
        services.cache.setInvoice([invoice])

        await this.checkAndExecutePaywallOnSuccessfulInvoiceProcessing({
            newInvoices: 1,
        })

        return invoice
    }

    private async extractInvoiceData(
        pdfText: string,
    ): Promise<RawInvoiceMetadata | undefined> {
        const { llms } = this.deps.getServices()
        const adaptedGetReply = <T = unknown>(params: {
            model: string
            messages: LLMRequestMessage<string | LLMRequestContent[]>[]
            jsonResponseSchema?: LLMResponseJSONSchema
            plugins?: LLMPlugin[]
        }) =>
            llms.getReply<T>({
                ...params,
                stream: false,
                onlyGoogleVertex: true,
            }) as Promise<T>
        const service = new InvoiceExtractionService({
            getReply: adaptedGetReply,
        })
        const raw = await service.extractRawFromText(pdfText)
        return raw
    }

    private async fetchExternalEmailHTML(
        emailExternalId: string,
        accountEmail?: string,
    ): Promise<string | null> {
        const { emailHtmlCache } = this.deps.localStorageModules
        const services = this.deps.getServices()

        // Try local cache first
        const cached = await emailHtmlCache.getHtml(emailExternalId)
        if (cached) {
            return cached
        }

        // Fallback to Gmail API and cache the result
        const emailData = await services.googleApi.getGmailMessage({
            messageId: emailExternalId,
            accountEmail,
        })
        const htmlContent = parseFullMessage(emailData).htmlContent
        if (htmlContent) {
            await emailHtmlCache.setHtml(emailExternalId, htmlContent)
        }
        return htmlContent
    }

    private async classifyPotentialInvoiceTextViaLLM(
        text: string,
    ): Promise<InvoiceClassificationResult> {
        const { llms } = this.deps.getServices()

        const reply = await retryWithValidation({
            operation: () =>
                llms.getReply<InvoiceClassificationResult>({
                    model: INVOICE_CLASSIFICATION_MODEL,
                    jsonResponseSchema: INVOICE_CLASSIFICATION_JSON_SCHEMA,
                    messages: [
                        {
                            role: 'system',
                            content: INVOICE_CLASSIFICATION_PROMPT,
                        },
                        {
                            role: 'user',
                            content: text,
                        },
                    ],
                }) as Promise<InvoiceClassificationResult>,
            validator: (result) => this.validateClassificationResponse(result),
            maxRetries: 2,
            // If all retries fail, return a default classification
            onFailure: () => {
                throw new Error('Failed to classify invoice')
            },
        })

        // TODO-audit: why is `receipt` even an option if we just override it here?
        if (reply.classification === ('receipt' as InvoiceClass)) {
            reply.classification = 'invoice'
        }

        return reply
    }

    private validateClassificationResponse(
        response: unknown,
    ): response is InvoiceClassificationResult {
        // Check if response is an object
        if (!response || typeof response !== 'object') {
            return false
        }

        // Check if classification key exists
        if (!('classification' in response)) {
            return false
        }

        // Check if classification has a valid value
        const { classification } = response
        if (
            classification !== 'invoice' &&
            classification !== 'receipt' &&
            classification !== 'other'
        ) {
            return false
        }

        return true
    }
    private queueVendorFaviconFetch(fromEmail: string): void {
        const services = this.deps.getServices()
        // Queue favicon fetch without waiting for it to complete
        services.favicon.getFaviconForEmail(fromEmail).catch((error) => {
            console.warn(
                `Failed to queue favicon fetch for ${fromEmail}:`,
                error,
            )
        })
    }

    // TODO-audit: should move to a util function
    private specialInvoiceRules(email: GmailMessageParsed): boolean {
        if (email.subject?.toLowerCase()?.includes('you made a sale')) {
            return false
        }
        if (
            (email.subject
                ?.toLowerCase()
                ?.includes('you received a transfer') ||
                email.subject
                    ?.toLowerCase()
                    ?.includes('you just made a payment')) &&
            email.fromEmail?.toLowerCase()?.includes('qonto.com')
        ) {
            return false
        }
        return true
    }
}
